// ═══════════════════════════════════════════════════════════
// «Документ із шаблону» — з картки клієнта або з візиту (24.10).
//
// Вибрали шаблон → (якщо в ньому є поля візиту, а візит не заданий —
// вибрали візит) → заповнили ручні поля за їхнім типом → сервер заповнює
// шаблон і кладе .docx у документи клієнта. Поля системи (клієнт, візит,
// заклад, дата) заповнюються самі.
// ═══════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { apiFetch } from '../../services/api';
import { useCloseOnEscape } from './docFiles';

const fmtVisit = (a) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(a.date || ''));
  const d = m ? `${m[3]}.${m[2]}.${m[1]}` : '';
  return [d, a.startTime, a.serviceName].filter(Boolean).join(' · ') || `Візит ${a.id}`;
};

function FieldInput({ field, value, onChange }) {
  switch (field.type) {
    case 'date':
      return <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'number':
      return <input type="number" inputMode="decimal" value={value ?? ''} onChange={e => onChange(e.target.value)} />;
    case 'yesno':
      return (
        <div className="tpl-yesno">
          {[['yes', 'Так'], ['no', 'Ні']].map(([v, label]) => (
            <label key={v} className={`tpl-choice${value === v ? ' selected' : ''}`}>
              <input type="radio" name={`yn_${field.name}`} checked={value === v} onChange={() => onChange(v)} /> {label}
            </label>
          ))}
        </div>
      );
    case 'select':
      if (field.multiple) {
        const list = Array.isArray(value) ? value : [];
        return (
          <div className="tpl-multi">
            {(field.options || []).map(o => (
              <label key={o} className={`tpl-choice${list.includes(o) ? ' selected' : ''}`}>
                <input type="checkbox" checked={list.includes(o)}
                  onChange={e => onChange(e.target.checked ? [...list, o] : list.filter(x => x !== o))} /> {o}
              </label>
            ))}
            {!field.options?.length && <span className="api-keys-muted">Варіантів ще немає — додайте їх у налаштуваннях шаблону.</span>}
          </div>
        );
      }
      return (
        <select value={value || ''} onChange={e => onChange(e.target.value)}>
          <option value="">— вибрати —</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    default:
      return <textarea rows={2} value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
}

export default function DocumentFromTemplateModal({ customer, appointmentId = null, visits = [], locationId = null, canManage = false, onCreated, onClose }) {
  const toast = useToast();
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [visitId, setVisitId] = useState(appointmentId ? String(appointmentId) : '');
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  useCloseOnEscape(onClose);

  useEffect(() => {
    apiFetch('/api/document-templates?action=list')
      .then(r => {
        setTemplates(r.templates || []);
        if (r.templates?.length === 1) setTemplateId(r.templates[0].id);
      })
      .catch(err => { toast.error(err?.body?.message || err?.message || 'Не вдалося завантажити шаблони'); setTemplates([]); });
  }, [toast]);

  const sortedVisits = useMemo(() => [...(visits || [])]
    .filter(a => a && a.id != null && a.status !== 'cancelled')
    .sort((a, b) => `${b.date || ''} ${b.startTime || ''}`.localeCompare(`${a.date || ''} ${a.startTime || ''}`)), [visits]);

  const tpl = (templates || []).find(t => t.id === templateId) || null;
  const needVisit = !!tpl?.usesVisit && !appointmentId;

  useEffect(() => {
    setValues({});
    if (needVisit && !visitId && sortedVisits[0]) setVisitId(String(sortedVisits[0].id));
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    if (!tpl || busy) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/document-templates?action=generate', {
        method: 'POST',
        body: {
          templateId: tpl.id,
          customerId: customer?.id || null,
          appointmentId: appointmentId || (tpl.usesVisit ? visitId || null : null),
          locationId,
          values,
        },
      });
      toast.success('Документ створено');
      onCreated?.(r.document);
    } catch (err) {
      toast.error(err?.body?.message || err?.message || 'Не вдалося створити документ');
    }
    setBusy(false);
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal-sm tpl-gen-modal" role="dialog" aria-modal="true" aria-label="Документ із шаблону">
        <div className="modal-header">
          <h2>📄 Документ із шаблону</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Закрити">✕</button>
        </div>
        <div className="modal-body tpl-gen-body">
          {templates === null && <p className="api-keys-muted">Завантаження…</p>}
          {templates?.length === 0 && (
            <p className="api-keys-muted">
              Шаблонів ще немає.{canManage ? ' Завантажте свій Word-шаблон у «Клієнти → Шаблони документів».' : ' Попросіть адміністратора завантажити шаблони.'}
            </p>
          )}
          {templates?.length > 0 && (
            <>
              <div className="form-field">
                <label className="field-label">Шаблон</label>
                <select value={templateId} onChange={e => setTemplateId(e.target.value)}>
                  <option value="">— вибрати —</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>

              {needVisit && (
                <div className="form-field">
                  <label className="field-label">Візит</label>
                  <select value={visitId} onChange={e => setVisitId(e.target.value)}>
                    <option value="">— без візиту —</option>
                    {sortedVisits.map(a => <option key={a.id} value={String(a.id)}>{fmtVisit(a)}</option>)}
                  </select>
                  <small className="api-keys-muted">Звідси підставляться дата, час, послуга, спеціаліст і ціна.</small>
                </div>
              )}

              {tpl && tpl.fields.length > 0 && (
                <div className="tpl-gen-fields">
                  {tpl.fields.map(f => (
                    <div key={f.name} className="form-field">
                      <label className="field-label">{f.name}</label>
                      <FieldInput field={f} value={values[f.name]} onChange={v => setValues(prev => ({ ...prev, [f.name]: v }))} />
                    </div>
                  ))}
                </div>
              )}
              {tpl && (
                <p className="api-keys-muted tpl-gen-hint">
                  Решту ({tpl.systemFields.length ? tpl.systemFields.join(', ') : 'нічого'}) система заповнить сама. Документ збережеться в документах клієнта.
                </p>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Скасувати</button>
          <button type="button" className="btn btn-primary" onClick={create} disabled={!tpl || busy}>
            {busy ? 'Створюємо…' : 'Створити документ'}
          </button>
        </div>
      </div>
    </div>
  );
}
